import {
  ScopeRequiredError,
  type ActiveDataFilter,
  type DashboardFilters,
} from "@nexus/widget-sdk";
import type { DashboardDefinition, DashboardScopeConfig, WidgetInstance } from "./types";
import { reportTenantState } from "./reportScope";

const defaultBindings = {
  event_id: { pathParam: "eventId", queryParam: "event_id" },
  protected_event_id: { pathParam: "protectedEventId", queryParam: "protected_event_id" },
  campaign_id: { pathParam: "campaignId", queryParam: "campaign_id" },
  tenant_id: { pathParam: "tenantId", queryParam: "tenant_id" },
} as const;

export function storedDashboardTenantIds(
  config: DashboardScopeConfig | undefined,
): string[] | null {
  const selection = config?.parameters?.tenant_id;
  if (selection?.operator !== "in" || !Array.isArray(selection.value)) return null;
  return selection.value.map(String).filter((id) => /^\d+$/.test(id) && Number(id) > 0);
}

export function resolveDashboardRouteScope(
  config: DashboardScopeConfig | undefined,
  params: Readonly<Record<string, string | undefined>>,
  search: URLSearchParams,
) {
  function read(field: keyof typeof defaultBindings) {
    const binding = config?.bindings?.[field] ?? defaultBindings[field];
    return (
      (binding.pathParam ? params[binding.pathParam] : undefined) ||
      (binding.queryParam ? search.get(binding.queryParam) : null) ||
      null
    );
  }
  const parameters: ActiveDataFilter[] = Object.entries(config?.parameters ?? {}).flatMap(
    ([field, binding]) => {
      if (field in defaultBindings) return [];
      const pathValue = binding.pathParam ? params[binding.pathParam] : undefined;
      const queryValues = binding.queryParam ? search.getAll(binding.queryParam) : [];
      const operator = binding.operator ?? "eq";
      const value =
        pathValue !== undefined
          ? operator === "in"
            ? [pathValue]
            : pathValue
          : queryValues.length
            ? operator === "in"
              ? queryValues
              : queryValues[0]
            : binding.value;
      if (value === null || value === undefined) return [];
      return [
        {
          key: `dashboard-parameter:${field}`,
          field,
          operator,
          value,
          source: {
            widgetInstanceId: "dashboard-context",
            dataKey: "parameters",
            filterKey: field,
          },
        },
      ];
    },
  );
  return {
    ...(config?.parameters ? { parameters } : {}),
    eventId: read("event_id"),
    protectedEventId: config?.protectedEvents === "none" ? null : read("protected_event_id"),
    campaignId: read("campaign_id"),
    tenantId: config?.tenants === "all" ? null : read("tenant_id"),
  };
}

export function mergeStoredDashboardScope({
  config,
  route,
  filters,
  selectedTenantIds,
  selectedEventIds,
  widgets = [],
}: {
  config?: DashboardScopeConfig;
  route: ReturnType<typeof resolveDashboardRouteScope>;
  filters: DashboardFilters;
  selectedTenantIds: string[];
  selectedEventIds: string[];
  widgets?: readonly WidgetInstance[];
}): DashboardFilters {
  const values = {
    event_id: route.eventId ?? (config?.events === "none" ? null : selectedEventIds[0]),
    protected_event_id: route.protectedEventId,
    campaign_id: route.campaignId,
  };
  const scopeFilters: ActiveDataFilter[] = Object.entries(values).flatMap(([field, value]) =>
    value
      ? [
          {
            key: `global-scope:${field}`,
            field,
            operator: "eq" as const,
            value,
            source: {
              widgetInstanceId: "global-scope",
              dataKey: "live-dashboard",
              filterKey: field,
            },
          },
        ]
      : [],
  );
  scopeFilters.push(...(route.parameters ?? []));
  const scopedFields = new Set(scopeFilters.map((filter) => filter.field));
  const reports = reportTenantState(widgets);
  const ownedTenants =
    storedDashboardTenantIds(config) ??
    (!reports.mixed && reports.ids.length > 0 ? reports.ids.map(String) : null);
  const selectedOwnedTenants = ownedTenants?.filter((id) => selectedTenantIds.includes(id));
  const tenantIds = ownedTenants
    ? selectedOwnedTenants?.length
      ? selectedOwnedTenants
      : ownedTenants
    : config?.tenants === "all"
      ? []
      : selectedTenantIds;
  if (ownedTenants && tenantIds.length === 0)
    throw new ScopeRequiredError("The selected tenants are not covered by this dashboard.");
  return {
    ...filters,
    tenantIds,
    dataFilters: [
      ...filters.dataFilters.filter((filter) => !scopedFields.has(filter.field)),
      ...scopeFilters,
    ],
  };
}

export function bindDashboardWidgetScope(
  dashboard: DashboardDefinition,
  filters: DashboardFilters,
): DashboardDefinition {
  function bind(widget: WidgetInstance): WidgetInstance {
    if (!widget.configBindings) return widget;
    const config = { ...widget.config };
    for (const [property, field] of Object.entries(widget.configBindings)) {
      const value =
        field === "tenant_id"
          ? filters.tenantIds.length === 1
            ? filters.tenantIds[0]
            : null
          : filters.dataFilters.find((filter) => filter.field === field)?.value;
      config[property] = value ?? null;
    }
    return { ...widget, config };
  }
  return {
    ...dashboard,
    widgets: dashboard.widgets.map(bind),
    sections: dashboard.sections?.map((section) => ({
      ...section,
      widgets: section.widgets.map(bind),
    })),
  };
}

/** What a generated-report page tells its dashboard: the edition and its frozen scope. */
export interface ReportDashboardContext {
  reportInstanceId: string;
  period?: { start: string; end: string };
  scope: {
    tenant_ids: number[];
    campaign_ids?: string[] | null;
    protected_event_ids?: string[] | null;
  };
}

function single<T>(values: readonly T[] | null | undefined): T | null {
  return values && values.length === 1 ? values[0] : null;
}

/**
 * Entities a report edition pins by itself: a report covering exactly one
 * campaign (or protected event, or tenant) opens its templates on that entity
 * without it being in the URL. The route still wins when it names one.
 */
export function reportScopeEntities(context: ReportDashboardContext | undefined): {
  tenantId: number | null;
  campaignId: string | null;
  protectedEventId: string | null;
} {
  return {
    tenantId: single(context?.scope.tenant_ids),
    campaignId: single(context?.scope.campaign_ids),
    protectedEventId: single(context?.scope.protected_event_ids),
  };
}

/** The drill-down level a report edition starts at, from the narrowest entity it pins. */
export function reportRootLevel(
  context: ReportDashboardContext | undefined,
): "portfolio" | "tenant" | "campaign" | "event" {
  const entities = reportScopeEntities(context);
  if (entities.protectedEventId) return "event";
  if (entities.campaignId) return "campaign";
  if (entities.tenantId !== null && context?.scope.tenant_ids.length === 1) return "tenant";
  return "portfolio";
}

/**
 * Scope filters a report page adds to its dashboard: the edition
 * (`report_instance_id`) and, for a single-tenant edition, `tenant_id` — read
 * by snapshot sources through their `contextParams`.
 */
export function reportContextFilters(
  context: ReportDashboardContext | undefined,
): ActiveDataFilter[] {
  if (!context) return [];
  const { tenantId } = reportScopeEntities(context);
  const values: Record<string, string> = { report_instance_id: context.reportInstanceId };
  if (tenantId !== null) values.tenant_id = String(tenantId);
  return Object.entries(values).map(([field, value]) => ({
    key: `report-context:${field}`,
    field,
    operator: "eq" as const,
    value,
    source: { widgetInstanceId: "report-context", dataKey: "report", filterKey: field },
  }));
}
